import React, { useState } from 'react';
import { useFeedbackCategories } from '../hooks/useFeedbackCategories';

interface GaugeChartProps {
  score: number; // Score from 0 to 1
  category: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  className?: string;
  showIcon?: boolean;
  categoryId?: string;
  onClick?: () => void;
  clickable?: boolean;
}

const GaugeChart: React.FC<GaugeChartProps> = ({
  score,
  category,
  sentiment,
  className = '',
  showIcon = false,
  categoryId,
  onClick,
  clickable = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const feedbackCategories = useFeedbackCategories();

  // Get category icon if showIcon is true and categoryId is provided
  const categoryIcon = React.useMemo(() => {
    if (!showIcon || !categoryId) return null;
    const foundCategory = feedbackCategories.find(
      (cat) => cat.id === categoryId
    );
    return foundCategory?.icon.dark || null;
  }, [showIcon, categoryId, feedbackCategories]);

  const getSentimentStrokeColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return '#6AC982'; // Green
      case 'negative':
        return '#EF4444'; // Red
      case 'neutral':
        return '#F59E0B'; // Yellow
      default:
        return '#6B7280'; // Grey
    }
  };

  // Convert score to percentage relative to 0.5 (neutral point)
  const getPercentageFromScore = (score: number) => {
    const percentage = (score - 0.5) * 200; // Convert to percentage where 0.5 = 0%
    return percentage > 0
      ? `+${percentage.toFixed(0)}%`
      : `${percentage.toFixed(0)}%`;
  };

  // Calculate the full circle gauge parameters
  const radius = 35;
  const circumference = 2 * Math.PI * radius;

  // Calculate progress length based on distance from neutral point (0.5)
  // Score 0.5 = 0% progress, Score 0 or 1 = 100% progress
  const progressPercentage = Math.abs(score - 0.5) * 2; // 0 to 1
  const progressLength = progressPercentage * circumference;

  // Set up stroke-dasharray and stroke-dashoffset
  const strokeDasharray = `${progressLength} ${circumference}`;

  // Calculate rotation: start from 12 o'clock (-90 degrees)
  // For positive scores (> 0.5): rotate clockwise
  // For negative scores (< 0.5): rotate counterclockwise
  let rotation = -90; // Start at 12 o'clock
  if (score < 0.5) {
    // For negative scores, we want to go counterclockwise
    // We need to rotate the starting point and reverse the direction
    rotation = -90 - progressPercentage * 360;
  }

  // Calculate end indicator position
  const calculateEndIndicatorPosition = () => {
    if (progressPercentage === 0) return null; // Don't show indicator if no progress
    
    let endAngle;
    if (score >= 0.5) {
      // Clockwise rotation: start at -90° and go clockwise
      endAngle = -90 + (progressPercentage * 360);
    } else {
      // Counterclockwise rotation: start at -90° and go counterclockwise
      endAngle = -90 - (progressPercentage * 360);
    }
    
    // Convert angle to radians
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    // Calculate x, y coordinates (center is at 50, 50)
    const x = 50 + radius * Math.cos(endAngleRad);
    const y = 50 + radius * Math.sin(endAngleRad);
    
    return { x, y };
  };

  const endIndicatorPosition = calculateEndIndicatorPosition();

  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  return (
    <div
      className={`relative flex flex-col items-center ${className} ${
        clickable && onClick 
          ? 'cursor-pointer hover:scale-110 transform transition-transform duration-300 ease-out' 
          : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {/* SVG Gauge Chart */}
      <div className="relative w-24 h-24">
        <svg className="w-full h-full" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="6"
          />

          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={getSentimentStrokeColor(sentiment)}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset="0"
            className="transition-all duration-1000 ease-out"
            transform={`rotate(${rotation} 50 50)`}
          />

          {/* End indicator (dot) */}
          {endIndicatorPosition && (
            <circle
              cx={endIndicatorPosition.x}
              cy={endIndicatorPosition.y}
              r="5"
              fill={getSentimentStrokeColor(sentiment)}
              stroke={getSentimentStrokeColor(sentiment)}
              strokeWidth="2"
              className="transition-all duration-1000 ease-out"
              style={{
                opacity: progressPercentage > 0 ? 1 : 0,
              }}
            />
          )}
        </svg>

        {/* Content in the center - Icon or Score on hover */}
        <div className="absolute inset-0 flex items-center justify-center">
          {isHovered ? (
            <div className="text-xs font-semibold text-fake-black">
              {getPercentageFromScore(score)}
            </div>
          ) : (
            categoryIcon && (
              <img
                className="w-8 h-8 object-cover"
                src={categoryIcon}
                alt={category}
              />
            )
          )}
        </div>
      </div>
      {/* Category name */}
      <div className="text-xs text-fake-black mt-2 text-center max-w-20 truncate">
        {category.replace('#', '')}
      </div>
    </div>
  );
};

export default GaugeChart;
